import { api } from "@/lib/apiClient";

export interface FernausleseLiveStatus {
  value_kwh: string | null;
  state: number | null;
  error: number | null;
  power_w: number | null;
  usage_kwh: string | null;
  target_kwh: string | null;
  quick_check_code: string | null;
  label: string | null;
  mode_desired: number | null;
  mode_actual: number | null;
  api_error: string | null;
}

export interface FernausleseOverviewRow {
  pitch_id: string;
  pitch_code: string;
  has_electricity: boolean;
  channel_id: number | null;
  guest_name: string | null;
  booking_number: string | null;
  wants_electricity: boolean | null;
  booking_electricity_kwh: string | null;
  live: FernausleseLiveStatus | null;
}

export interface FernausleseOverview {
  configured: boolean;
  live_loaded: boolean;
  mapped_channels: number;
  catalog_channels: number;
  rows: FernausleseOverviewRow[];
}

export interface FernausleseChannel {
  channel_id: number;
  description: string;
  pitch_id: string | null;
  pitch_code: string | null;
}

export interface FernausleseChannelList {
  channels: FernausleseChannel[];
  mapped_count: number;
  catalog_count: number;
}

export interface FernausleseChannelMappingSuggestion {
  channel_id: number;
  description: string;
  pitch_id: string | null;
  pitch_code: string | null;
  score: number | null;
  current_pitch_id: string | null;
  current_pitch_code: string | null;
}

export interface FernausleseChannelImportPreview {
  channels: FernausleseChannelMappingSuggestion[];
  duplicate_pitch_warnings: string[];
}

export interface FernausleseChannelMapping {
  channel_id: number;
  pitch_id: string | null;
}

export const FERNAUSLESE_STATE_LABELS: Record<number, string> = {
  0: "Aus",
  1: "An",
  2: "Guthaben",
  3: "Guthaben-Automatik",
  4: "Verbrauch-Automatik",
};

export function fernausleseStateLabel(state: number | null | undefined): string {
  if (state == null) return "—";
  return FERNAUSLESE_STATE_LABELS[state] ?? `Modus ${state}`;
}

/** Schaltzustände 1–4 bedeuten: Steckdose ist eingeschaltet (An, Guthaben, Automatik). */
export function isFernausleseChannelOn(state: number | null | undefined): boolean {
  return state === 1 || state === 2 || state === 3 || state === 4;
}

/** Anzeige am Platzplan: grün an, rot Versorgungsfehler, grau aus. */
export type PitchElectricityMarker = "on" | "off" | "fault";

export function electricityMarkerFromLive(
  live: FernausleseLiveStatus | null | undefined,
  hasElectricity: boolean,
): PitchElectricityMarker | null {
  if (!hasElectricity && !live) return null;
  if (isFernausleseSupplyFault(live?.error)) return "fault";
  if (isFernausleseChannelOn(live?.state)) return "on";
  if (hasElectricity || live) return "off";
  return null;
}

export function electricityMarkerLabel(status: PitchElectricityMarker): string {
  switch (status) {
    case "on":
      return "Strom an";
    case "off":
      return "Strom aus";
    case "fault":
      return "Stromfehler";
  }
}

/**
 * Fernauslese `error=1`: Stromversorgungsfehler im Schrank
 * (FI ausgelöst, Vorsicherung defekt oder Phase fehlt).
 * Kommunikationsfehler stehen nicht in diesem Feld.
 */
export function isFernausleseSupplyFault(error: number | null | undefined): boolean {
  return error === 1;
}

export const FERNAUSLESE_SUPPLY_FAULT_LABEL = "FI / Sicherung";

export const FERNAUSLESE_SUPPLY_FAULT_HINT =
  "FI-Schutzschalter ausgelöst, Sicherung defekt oder eine Phase fehlt. Im Verteilerschrank prüfen.";

export async function getFernausleseChannels(): Promise<FernausleseChannelList> {
  return api.get<FernausleseChannelList>("/api/v1/fernauslese/channels");
}

export async function getFernausleseOverview(live = true, fresh = false): Promise<FernausleseOverview> {
  const params = new URLSearchParams({ live: live ? "true" : "false" });
  if (fresh) params.set("fresh", "true");
  return api.get<FernausleseOverview>(`/api/v1/fernauslese/overview?${params}`);
}

export async function previewFernausleseChannelImport(
  csvContent: string,
): Promise<FernausleseChannelImportPreview> {
  return api.post<FernausleseChannelImportPreview>("/api/v1/fernauslese/channels/import/preview", {
    csv_content: csvContent,
  });
}

export async function applyFernausleseChannelImport(
  mappings: FernausleseChannelMapping[],
  csvContent?: string,
): Promise<{ updated: number; cleared: number; skipped: number }> {
  return api.post("/api/v1/fernauslese/channels/import/apply", {
    mappings,
    csv_content: csvContent,
  });
}
